import { apiUrl } from './runtime';

export type ApiErrorKind = 'offline' | 'server' | 'client' | 'aborted';

export class ApiError extends Error {
  readonly status: number;
  readonly kind: ApiErrorKind;
  readonly code?: string;

  constructor(message: string, status: number, kind: ApiErrorKind, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.kind = kind;
    this.code = code;
  }
}

export type ApiFetchOptions = {
  method?: string;
  /** JSON-serializable body (stringified automatically). */
  body?: unknown;
  /** Supabase access token — never logged. */
  token?: string | null;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

function isAbortError(err: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted) return true;
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  if (err instanceof Error && err.name === 'AbortError') return true;
  return false;
}

async function readErrorPayload(response: Response): Promise<{ message: string; code?: string }> {
  try {
    const data = await response.json();
    const code = typeof data?.code === 'string' && data.code.trim() ? data.code : undefined;
    if (data && typeof data.error === 'string' && data.error.trim()) {
      return { message: data.error, code };
    }
    if (data && typeof data.message === 'string' && data.message.trim()) {
      return { message: data.message, code };
    }
    return { message: `Request failed (${response.status})`, code };
  } catch {
    return { message: `Request failed (${response.status})` };
  }
}

/**
 * Central MovieMe-owned API client.
 * All `/api/*` calls should go through here so native builds can use an absolute HTTPS base.
 *
 * Direct TMDB fetches (`api.themoviedb.org`) remain outside this helper — see `tmdb.ts` / `openai.ts`.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const { method = 'GET', body, token, signal, headers: extraHeaders } = options;

  const headers: Record<string, string> = { ...(extraHeaders ?? {}) };

  if (body !== undefined && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    if (isAbortError(err, signal)) {
      throw new ApiError('Request aborted', 0, 'aborted');
    }
    throw new ApiError('Network unavailable. Check your connection and try again.', 0, 'offline');
  }

  if (!response.ok) {
    const { message, code } = await readErrorPayload(response);
    const kind: ApiErrorKind = response.status >= 500 ? 'server' : 'client';
    throw new ApiError(message, response.status, kind, code);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError('Invalid JSON response from server', response.status, 'server');
  }
}
